import type { RiskLevel } from "@/lib/api";

const RISK_CONFIG: Record<
  RiskLevel,
  { label: string; className: string; glowClass?: string }
> = {
  critical: {
    label: "CRITICAL",
    className:
      "bg-red-500/15 text-red-400 border-red-500/40 shadow-red-500/20",
    glowClass: "shadow-[0_0_12px_rgba(239,68,68,0.4)]",
  },
  high: {
    label: "HIGH",
    className:
      "bg-orange-500/15 text-orange-400 border-orange-500/40 shadow-orange-500/20",
    glowClass: "shadow-[0_0_10px_rgba(249,115,22,0.3)]",
  },
  medium: {
    label: "MEDIUM",
    className:
      "bg-amber-500/15 text-amber-400 border-amber-500/40 shadow-amber-500/20",
  },
  low: {
    label: "LOW",
    className:
      "bg-cyan-500/15 text-cyan-400 border-cyan-500/40 shadow-cyan-500/20",
  },
  info: {
    label: "INFO",
    className:
      "bg-slate-500/15 text-slate-400 border-slate-500/40 shadow-slate-500/20",
  },
};

interface RiskBadgeProps {
  level: RiskLevel;
  size?: "sm" | "md" | "lg";
  showGlow?: boolean;
  className?: string;
}

export function RiskBadge({
  level,
  size = "md",
  showGlow = false,
  className = "",
}: RiskBadgeProps) {
  const config = RISK_CONFIG[level];

  const sizeClasses = {
    sm: "px-1.5 py-0.5 text-[10px] tracking-wider",
    md: "px-2 py-1 text-xs tracking-wide",
    lg: "px-3 py-1.5 text-sm tracking-wide",
  };

  return (
    <span
      className={`
        inline-flex items-center justify-center
        font-mono font-semibold uppercase
        border rounded
        transition-all duration-200
        ${config.className}
        ${sizeClasses[size]}
        ${showGlow && config.glowClass ? config.glowClass : ""}
        ${className}
      `}
    >
      {/* Subtle pulse dot for critical/high */}
      {(level === "critical" || level === "high") && (
        <span
          className={`
            w-1.5 h-1.5 rounded-full mr-1.5
            ${level === "critical" ? "bg-red-400" : "bg-orange-400"}
            animate-pulse
          `}
        />
      )}
      {config.label}
    </span>
  );
}

// Compact variant for tables
export function RiskDot({ level }: { level: RiskLevel }) {
  const colors: Record<RiskLevel, string> = {
    critical: "bg-red-500",
    high: "bg-orange-500",
    medium: "bg-amber-500",
    low: "bg-cyan-500",
    info: "bg-slate-500",
  };

  return (
    <span
      className={`
        inline-block w-2 h-2 rounded-full
        ${colors[level]}
        ${level === "critical" ? "animate-pulse" : ""}
      `}
      title={level.toUpperCase()}
    />
  );
}

export default RiskBadge;
